import React, { useState } from 'react'
import useTranslation from '../../hooks/useTranslation'
import { supportedLanguages, languageNames, SupportedLanguage } from '../../i18n'

interface LanguageSelectorProps {
  variant?: 'default' | 'compact'
  className?: string
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({ 
  variant = 'default', 
  className = '' 
}) => {
  const { language, changeLanguage, isLoading } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)

  const handleLanguageChange = async (selectedLanguage: SupportedLanguage) => {
    if (selectedLanguage !== language && !isLoading) {
      await changeLanguage(selectedLanguage)
      setIsOpen(false)
    }
  }

  const toggleDropdown = () => {
    if (!isLoading) {
      setIsOpen(!isOpen)
    }
  }

  const baseClasses = "relative inline-block text-left"
  const compactClasses = variant === 'compact' ? 'w-auto' : 'w-40'

  return (
    <div className={`${baseClasses} ${compactClasses} ${className}`}>
      <div>
        <button
          type="button"
          className={`
            inline-flex justify-center items-center w-full rounded-md border border-gray-300 
            shadow-sm px-4 py-2 bg-white text-sm font-medium text-gray-700 
            hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 
            focus:ring-indigo-500 transition-colors duration-200
            ${isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          `}
          onClick={toggleDropdown}
          disabled={isLoading}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className="mr-2">🌐</span>
          {variant === 'compact' ? (
            <span className="uppercase font-semibold">{language}</span>
          ) : (
            <span>{languageNames[language]}</span>
          )}
          <span 
            className={`
              ml-2 transition-transform duration-200
              ${isOpen ? 'transform rotate-180' : ''}
              ${isLoading ? 'animate-spin' : ''}
            `} 
          >
            ▼
          </span>
        </button>
      </div>

      {isOpen && (
        <div className="
          origin-top-right absolute right-0 mt-2 w-full rounded-md shadow-lg 
          bg-white ring-1 ring-black ring-opacity-5 focus:outline-none z-50
        ">
          <div className="py-1" role="menu" aria-orientation="vertical">
            {supportedLanguages.map((lang) => (
              <button
                key={lang}
                className={`
                  w-full text-left px-4 py-2 text-sm transition-colors duration-150
                  ${
                    lang === language
                      ? 'bg-indigo-50 text-indigo-700 font-semibold'
                      : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                  }
                `}
                role="menuitem"
                onClick={() => handleLanguageChange(lang)}
                disabled={isLoading}
              >
                <div className="flex items-center justify-between">
                  <span>{languageNames[lang]}</span>
                  {lang === language && (
                    <span className="text-indigo-600">✓</span>
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default LanguageSelector